import { motion } from 'framer-motion'
import { Routes, Route, Navigate } from 'react-router-dom'
import { usePortfolioStore } from './store/portfolioStore'
import { useThemeStore } from './store/themeStore'
import { useAuthStore } from './store/authStore'
import { useTradingHistoryStore } from './store/tradingHistoryStore'
import Header from './components/Header'
import MobileTabBar from './components/MobileTabBar'
import Login from './components/Login'
import AddTransactionModal from './components/AddTransactionModal'
import AddGoldModal from './components/AddGoldModal'
import DeleteStockModal from './components/DeleteStockModal'
import CustomPortfolioModal from './components/CustomPortfolioModal'
import CsvImportModal from './components/CsvImportModal'
import LoadingSpinner from './components/LoadingSpinner'
import PortfolioPage from './components/pages/PortfolioPage'
import TradingPage from './components/pages/TradingPage'
import SettingsPage from './components/pages/SettingsPage'
import HeatmapPage from './components/pages/HeatmapPage'
import BankingPage from './components/pages/BankingPage'
import WatchlistPage from './components/pages/WatchlistPage'
import CreditPage from './components/pages/CreditPage'
import FxRateModal from './components/FxRateModal'
import NetWorthModal from './components/NetWorthModal'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'


function App() {
  const [authChecked, setAuthChecked] = useState(false)
  const [showFxRateModal, setShowFxRateModal] = useState(false)
  const [showNetWorthModal, setShowNetWorthModal] = useState(false)

  useEffect(() => {
    const handler = () => setShowFxRateModal(true)
    window.addEventListener('show-fx-modal', handler)
    return () => window.removeEventListener('show-fx-modal', handler)
  }, [])

  useEffect(() => {
    const handler = () => setShowNetWorthModal(true)
    window.addEventListener('show-net-worth-modal', handler)
    return () => window.removeEventListener('show-net-worth-modal', handler)
  }, [])
  
  const {
    showAddModal,
    showDeleteModal,
    showCustomPortfolioModal,
    showAddGoldModal,
    showCsvImportModal,
    loadPortfolio,
    loadLatestPrices,
    loadExchangeRate,
    loadCustomPortfolios,
    loadSectors,
    loadCountries,
    closeCsvImportModal,
    triggerDailySnapshot,
  } = usePortfolioStore()
  
  useThemeStore() // subscribe so theme changes re-render
  const { isAuthenticated, checkAuth } = useAuthStore()
  const { loadTradingHistory } = useTradingHistoryStore()

  // Check authentication on app load
  useEffect(() => {
    const initAuth = async () => {
      await checkAuth()
      setAuthChecked(true)
    }
    initAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Load initial data after authentication is confirmed
  useEffect(() => {
    if (!isAuthenticated || !authChecked) return
    
    const initializeApp = async () => {
      try {
        await Promise.all([
          loadPortfolio(),
          loadLatestPrices(),
          loadExchangeRate(),
          loadCustomPortfolios(),
          loadSectors(),
          loadCountries(),
          loadTradingHistory()
        ])
        // Save snapshot only after all data (holdings + prices + exchange rate) is fully loaded
        await triggerDailySnapshot()
      } catch (error) {
        console.error('Failed to initialize app:', error)
        toast.error('Failed to load portfolio data')
      }
    }

    initializeApp()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authChecked]) // Only re-initialize when auth state changes

  // Show loading while checking authentication instead of a blank white
  // screen (the blank screen is what reads as a "flash" on reload).
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app transition-colors duration-300">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />
  }

  // Show main app if authenticated
  return (
    <div className="min-h-screen bg-app transition-colors duration-300">
      {/* Background Pattern */}
      <div className="fixed inset-0 opacity-30">
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative z-10"
      >
        {/* Header */}
        <Header onFxRateClick={() => setShowFxRateModal(true)} />

        {/* Main Content */}
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-tabbar lg:pb-20">
          <Routes>
            <Route path="/" element={<PortfolioPage />} />
            <Route path="/trading" element={<TradingPage />} />
            <Route path="/banking" element={<BankingPage />} />
            <Route path="/heatmap" element={<HeatmapPage />} />
            <Route path="/watchlist" element={<WatchlistPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/credit" element={<CreditPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>


        {/* Same stacking context as the modals (z-50) so sheets cover it */}
        <MobileTabBar />

        {/* Modals */}
        <AddTransactionModal
          isOpen={showAddModal}
          onClose={() => usePortfolioStore.setState({ showAddModal: false })}
        />
        
        <DeleteStockModal 
          isOpen={showDeleteModal}
          onClose={() => usePortfolioStore.setState({ showDeleteModal: false })}
        />
        
        <CustomPortfolioModal 
          isOpen={showCustomPortfolioModal}
          onClose={() => usePortfolioStore.setState({ showCustomPortfolioModal: false, selectedPortfolio: null })}
        />
        
        <AddGoldModal 
          isOpen={showAddGoldModal}
          onClose={() => usePortfolioStore.setState({ showAddGoldModal: false })}
        />
        
        <CsvImportModal
          isOpen={showCsvImportModal}
          onClose={closeCsvImportModal}
        />

        <FxRateModal
          isOpen={showFxRateModal}
          onClose={() => setShowFxRateModal(false)}
        />

        <NetWorthModal
          isOpen={showNetWorthModal}
          onClose={() => setShowNetWorthModal(false)}
        />
      </motion.div>
    </div>
  )
}

export default App